import { useRef, useEffect } from 'react';
import { useCallbackOne } from 'use-memo-one';
import { BridgeContextObject, type BridgeContextInput } from './BridgeContextObject';
import type { BridgeContext, BridgeServices } from './types';

export { BRIDGE_CONTEXT_EVENTS } from './BridgeContextObject';

export const useBridgeContext = (
	props: BridgeContextInput,
	getServices: () => BridgeServices,
): {
	destroy: () => void;
	getBridgeContext: () => BridgeContext;
} => {
	// Created once for the lifetime of the component.
	// BridgeContextObject owns all memoization and change notification internally.
	const contextObjectRef = useRef<BridgeContextObject | null>(null);
	if (contextObjectRef.current === null) {
		contextObjectRef.current = new BridgeContextObject(props, getServices);
	}

	// Update after every commit. Bridge calls arrive via postRobot (async message events)
	// so the context will always be current before any bridge method is invoked.
	useEffect(() => {
		contextObjectRef.current!.update(props);
	});

	const getBridgeContext = useCallbackOne(() => contextObjectRef.current!.get(), []);
	const destroy = useCallbackOne(() => contextObjectRef.current!.destroy(), []);

	return { getBridgeContext, destroy };
};
